import {
  RequestFlow,
  requestFlow,
  requestStatus,
  RequestStatus,
} from 'src/lib/services/api/request-api';
import { generatePath } from 'react-router-dom';
import { appRoutes } from 'src/routes';
import { TFunction } from 'i18next';

export {
  DEFAULT_REQUEST_SORTS,
  withDefaultRequestSorts,
  isDefaultRequestSorts,
  resolveQueueAwareSorts,
} from './request.constants';

const getRequestNavigationRoute = (
  companyId: string,
  requestId: string,
  status: RequestStatus,
  flow: RequestFlow,
  requested_attention_at?: string | null,
) => {
  // Outcomes in Needs Attention land on the detail page so admins/editors
  // see the parent's Extras/Media Source/Details inherited context, not
  // the revisions timeline. Mirrors the queued-outcome path. See
  // AAG-Ventures/ai-agents-backend-service-v1#2431 (Karina follow-up).
  if (
    flow === requestFlow.multiOutcome ||
    flow === requestFlow.repurpose ||
    status === requestStatus.queued ||
    !!requested_attention_at
  ) {
    return generatePath(appRoutes.request, { companyId, requestId });
  }

  return generatePath(appRoutes.requestRevisions, { companyId, requestId });
};

const requestStatusTranslationsMap = (
  status: RequestStatus | 'archived' | 'all' | 'needs_attention' | 'needs_reedit',
) => {
  switch (status) {
    case 'draft':
      return 'Draft';
    case 'uploading':
      return 'Uploading...';
    case 'queued':
      return 'Queued';
    case 'currently_editing':
      return 'Editing';
    case 'delivered':
      return 'Ready for review';
    case 'needs_attention':
      return 'Needs Attention';
    case 'needs_reedit':
      return 'Needs Re-edit';
    case 'complete':
      return 'Approved';
    case 'archived':
      return 'Archived';
    case 'all':
      return 'All';
    default:
      return 'Unknown';
  }
};

/**
 * Title for the request-list "queue" view (the Priority Queue tab and its
 * kanban-column equivalent). The queued status list mirrors the client's
 * priority queue, so editors see "Priority Queue" there; per-request status
 * badges elsewhere keep the bare "Queued" label via
 * {@link requestStatusTranslationsMap}. (AAG #2478)
 */
const requestQueueViewLabel = (status: string) =>
  status === requestStatus.queued
    ? 'Priority Queue'
    : requestStatusTranslationsMap(status as Parameters<typeof requestStatusTranslationsMap>[0]);

// "Needs Attention" and "Needs Re-edit" used to live as separate substatus
// pills beside the status pill on the admin table; per #2383 the column is
// gone and the pill itself absorbs them. Attention is urgency-first (editor
// blocked) and wins over an in-flight re-edit cycle. Re-edit only overrides
// while the row is still queued — once the editor moves it to editing /
// delivered the underlying status takes over and the loader-trail icon
// signals the cycle is still in flight.
const resolveDisplayedRequestStatus = (request: {
  status?: string | null;
  requested_attention_at?: string | null;
  requested_reedit_at?: string | null;
}): string => {
  if (request.requested_attention_at) {
    return 'needs_attention';
  }
  if (request.requested_reedit_at && request.status === requestStatus.queued) {
    return 'needs_reedit';
  }
  return request.status ?? 'unknown';
};

export const formatDueDate: (t: TFunction, dueDate: string | Date) => string = (t, dueDate) => {
  // Parse as local date to prevent UTC-to-local timezone shift
  // e.g. "2026-03-16T01:00:00Z" in UTC-5 would show Mar 15 instead of Mar 16
  const dateStr = typeof dueDate === 'string' ? dueDate : dueDate.toISOString();
  const [year, month, day] = dateStr.split('T')[0].split('-').map(Number);
  const localDate = new Date(year, month - 1, day);

  // Safety net: if the date falls on a weekend, shift to next Monday
  const dayOfWeek = localDate.getDay();
  if (dayOfWeek === 6) localDate.setDate(localDate.getDate() + 2); // Saturday → Monday
  if (dayOfWeek === 0) localDate.setDate(localDate.getDate() + 1); // Sunday → Monday

  return t('Estimated due date: {{val, datetime}}', {
    val: localDate,
    formatParams: {
      val: { year: 'numeric', month: 'short', day: 'numeric' },
    },
  });
};

export const formatEditingTime: (t: TFunction, editingTime: number) => string = (
  t,
  editingTimeMinutes,
) => {
  if (editingTimeMinutes >= 60) {
    const hours = Math.floor(editingTimeMinutes / 60);
    const minutes = editingTimeMinutes % 60;
    return t('{{hours}} h {{minutes}} min', { hours, minutes });
  }

  return t('{{val}} min', { val: editingTimeMinutes });
};

export {
  getRequestNavigationRoute,
  requestStatusTranslationsMap,
  requestQueueViewLabel,
  resolveDisplayedRequestStatus,
};
